import type { Atom } from "@pdfking/tfunc"
import type { IterableLike } from "../types/IterableLike"
import { Items } from "@pdfking/tfunc"
import { is_mapping } from "./is_mapping"
import { ordered_object } from "./ordered_object"
import { to_array } from "./to_array"

/**
 * Filters iterable by predicate function.
 *
 * For objects, returns filtered object. For arrays, returns filtered array.
 * Predicate receives (value, key/index).
 *
 * @param f - Predicate function
 * @param iterable - Array, object, or primitive to filter
 * @returns Filtered result matching input type
 *
 * @example
 * ```ts
 * import { filter } from "@pdfking/rfunc"
 *
 * filter(v => v < 3, [1, 2, 3]) // [1, 2]
 * filter(v => v < 3, { a: 2, b: 5 }) // { a: 2 }
 * ```
 */
export function filter<T extends Atom | null | undefined>(
  f: (value: T, key: number) => unknown,
  iterable: T
): T[]
export function filter<T>(
  f: (value: T, key: number) => unknown,
  iterable: T[]
): T[]
export function filter<T>(
  f: (value: T, key: number) => unknown,
  iterable: Iterable<T>
): T[]
export function filter<K extends string, T>(
  f: (value: T, key: K) => unknown,
  iterable: Record<K, T>
): Partial<Record<K, T>>
export function filter<T>(
  f: (value: T, key: never) => unknown,
  iterable: IterableLike<T>
): T[] | Record<string, T> {
  if (is_mapping(iterable)) {
    return ordered_object(
      Items(iterable).filter(a => f(a[1] as T, a[0] as never)) as [string, T][]
    )
  }
  return to_array(iterable).filter((v, i) => f(v, i as never))
}
